import React from 'react';
import heroImage from '../../assets/hero-doctor.png'; 
import NavPhc from '../../components/Home/NavPhc';
import Footer from "../../components/Home/Footer";
import SolutionsSection from '../../components/Home/SolutionSection';
import BenefitsSection from '../../components/Home/Benefit'; 

// --- STATIC DATA CONFIGURATION ---
const featuresData = [
    { title: "Predict", desc: "Identify health risks and warning signs earlier." },
    { title: "Prevent", desc: "Enable timely screening, intervention and preventive care." },
    { title: "Connect", desc: "Bring together patients, doctors, hospitals and diagnostics across the healthcare journey." },
    { title: "Act", desc: "Help patients reach the right care, at the right time." },
    { title: "Improve Outcomes", desc: "Support continuous monitoring and follow-up beyond the hospital visit." }
];

const journeySteps = ["Prevent", "Detect", "Diagnose", "Treat", "Monitor", "Recover"];

const ecosystemData = [
    { title: "Patients", desc: "Know and manage your health journey." },
    { title: "Doctors", desc: "Get a more complete view of the patient's history." },
    { title: "Hospitals", desc: "Connect care beyond your institution." },
    { title: "Diagnostics", desc: "Make diagnostic information part of the connected patient journey." },
    { title: "Care Partners", desc: "Extend healthcare closer to where patients live." }
];

const visionData = [
    "Predict earlier", 
    "Prevent sooner", 
    "Treat smarter", 
    "Follow continuously", 
    "Improve outcomes"
];

const DoctorAI = () => {
    return (
        <div className="flex flex-col min-h-screen bg-[#fbfcff] selection:bg-blue-100 selection:text-blue-900 font-sans">
            <NavPhc />
            
            <main className="flex-grow">
            
                {/* HERO SECTION */}
                <section className="container mx-auto px-4 md:px-8 pt-12 md:pt-20 pb-16 md:pb-24 max-w-[85rem] relative overflow-hidden">
                    <div className="absolute top-0 right-0 -z-10 w-[500px] h-[500px] bg-blue-100/50 rounded-full mix-blend-multiply filter blur-[80px] transform translate-x-1/3 -translate-y-1/4"></div>
                    
                    <div className="inline-flex items-center space-x-2 text-blue-700 bg-blue-50/80 border border-blue-100 rounded-full px-5 py-2 text-xs font-bold uppercase tracking-widest mb-8 shadow-sm backdrop-blur-sm">
                        <span className="text-blue-500 text-lg leading-none">✦</span>
                        <span>AI-Powered HealthCare Ecosystem</span>
                    </div> 

                    <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
                        <div className="text-center lg:text-left z-10">
                            <h1 className="text-4xl md:text-5xl lg:text-6xl xl:text-[4rem] font-extrabold text-gray-900 leading-[1.1] mb-6 tracking-tight">
                                Advanced solutions to{" "}
                                <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-cyan-500">
                                    transform care & improve outcomes.
                                </span>
                            </h1>
                            <p className="text-base md:text-lg text-gray-600 leading-relaxed mb-8 max-w-2xl mx-auto lg:mx-0 font-medium">
                                MNR Technologies offers comprehensive healthcare solutions that address the complex challenges facing modern providers. From electronic health records to telemedicine platforms, we leverage cutting-edge technology to help organizations deliver better care while managing costs.
                            </p>
                        </div>

                        <div className="flex justify-center relative group">
                            <div className="absolute inset-0 bg-gradient-to-tr from-blue-600 to-cyan-400 rounded-3xl transform rotate-3 scale-[1.03] opacity-10 transition-transform duration-700 group-hover:rotate-6"></div>
                            <img 
                                src={heroImage} 
                                alt="A compassionate doctor interacting with a patient" 
                                className="relative rounded-3xl shadow-2xl w-full max-w-xl h-[400px] md:h-[500px] lg:h-[600px] object-cover border border-white/60 transition-transform duration-700 group-hover:-translate-y-2" 
                            />
                        </div>
                    </div>
                </section>

                {/* SECTION 2 — THE PROBLEM */}
                <section className="bg-white py-24 px-4 md:px-8 border-y border-gray-100 shadow-[0_4px_40px_rgba(0,0,0,0.02)] relative z-10">
                    <div className="max-w-4xl mx-auto text-center space-y-10">
                        <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-gray-900 leading-tight tracking-tight">
                            Healthcare is connected by institutions. <br className="hidden md:block"/>
                            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-blue-400">The patient journey is not.</span>
                        </h2>
                        <p className="text-lg md:text-xl text-gray-500 font-medium max-w-2xl mx-auto">
                            A patient's healthcare story is often scattered across hospitals, doctors, diagnostic centres and different systems.
                        </p>
                        <div className="inline-block bg-[#f8faff] shadow-lg shadow-blue-900/5 rounded-2xl p-8 md:p-10 border border-blue-100 transform transition duration-500 hover:-translate-y-1 hover:shadow-xl">
                            <p className="text-xl md:text-2xl font-semibold text-gray-800 leading-relaxed">
                                <span className="text-blue-600 font-bold">DoctorAI</span> brings the journey together — helping healthcare move from fragmented and reactive care to connected and proactive care.
                            </p>
                        </div>
                    </div>
                </section>

                {/* SECTION 3 — WHAT DOCTORAI DOES */}
                <section className="py-24 px-4 md:px-8 max-w-[90rem] mx-auto">
                    <div className="text-center mb-16">
                        <p className="text-blue-600 font-bold tracking-widest uppercase text-xs mb-3">What DoctorAI Does</p>
                        <h2 className="text-3xl md:text-5xl font-extrabold text-gray-900 tracking-tight">From Data to Better Care</h2>
                    </div>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6 lg:gap-8">
                        {featuresData.map((item, i) => (
                            <div key={i} className="group bg-white rounded-[2rem] p-8 md:p-10 flex flex-col justify-center text-center shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-gray-100/80 hover:shadow-[0_20px_40px_rgb(0,0,0,0.08)] hover:border-blue-100 transition-all duration-500 hover:-translate-y-2 relative overflow-hidden h-full min-h-[220px]">
                                <div className="absolute top-0 left-0 w-full h-1.5 bg-gradient-to-r from-blue-500 to-cyan-400 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                                <h3 className="text-2xl font-extrabold text-gray-900 mb-4 tracking-tight group-hover:text-blue-600 transition-colors duration-300">{item.title}</h3>
                                <p className="text-gray-500 text-sm md:text-base font-medium leading-relaxed">{item.desc}</p>
                            </div>
                        ))}
                    </div>
                </section>

                {/* SECTION 4 — THE BIG VISUAL */}
                <section className="bg-gray-900 py-24 px-4 md:px-8 text-white text-center relative overflow-hidden">
                    <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-blue-900/40 via-gray-900 to-gray-900"></div>
                    <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-600/20 rounded-full mix-blend-screen filter blur-[100px]"></div>
                    
                    <div className="max-w-6xl mx-auto relative z-10">
                        <h2 className="text-3xl md:text-5xl lg:text-6xl font-extrabold mb-16 tracking-tight text-white">One Patient. <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500">One Connected Journey.</span></h2>
                        
                        <div className="flex flex-wrap justify-center items-center gap-2 md:gap-4 text-sm md:text-lg font-bold mb-20">
                            {journeySteps.map((step, index, arr) => (
                                <React.Fragment key={index}>
                                    <div className="group flex flex-col items-center">
                                        <span className="bg-white/5 border border-white/10 hover:bg-blue-600/20 hover:border-blue-500/50 px-6 py-3 md:px-8 md:py-4 rounded-2xl backdrop-blur-md shadow-lg transition-all duration-300 cursor-default text-gray-200 hover:text-white">
                                            {step}
                                        </span>
                                    </div>
                                    {index < arr.length - 1 && (
                                        <span className="text-gray-500 text-xl font-light mx-1">➔</span>
                                    )}
                                </React.Fragment>
                            ))}
                        </div>

                        <div className="bg-white/5 border border-white/10 backdrop-blur-sm p-8 md:p-10 rounded-[2rem] max-w-4xl mx-auto shadow-2xl">
                            <p className="text-xl md:text-2xl font-light leading-relaxed text-gray-300">
                                Your healthcare journey doesn't end when you leave the hospital. <br className="hidden md:block mt-3"/>
                                <strong className="font-bold text-white text-2xl md:text-3xl block mt-2">DoctorAI is designed to help keep care connected across every stage.</strong>
                            </p>
                        </div>
                    </div>
                </section>

                {/* SECTION 5 — CONNECTED HEALTHCARE ECOSYSTEM (Icons Removed) */}
                <section className="py-24 px-4 md:px-8 max-w-[85rem] mx-auto bg-[#fbfcff]">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-gray-900 tracking-tight">Connecting the Ecosystem</h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
                        {ecosystemData.map((item, i) => (
                            <div key={i} className="group relative overflow-hidden flex flex-col justify-center items-center text-center p-8 md:p-10 bg-white rounded-[2rem] shadow-[0_4px_20px_rgb(0,0,0,0.03)] hover:shadow-[0_10px_40px_rgb(0,0,0,0.08)] transition-all duration-300 border border-gray-100 hover:border-blue-100 hover:-translate-y-1 min-h-[200px]">
                                <div className="absolute top-0 left-0 w-full h-1.5 bg-gradient-to-r from-blue-500 to-cyan-400 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                                <h3 className="text-2xl font-extrabold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors tracking-tight">{item.title}</h3>
                                <p className="text-gray-500 leading-relaxed text-sm md:text-base font-medium">{item.desc}</p>
                            </div>
                        ))}
                    </div>
                </section>

                {/* SECTION 6 — LAST-MILE HEALTHCARE */}
                <section className="bg-blue-600 text-white py-24 px-4 md:px-8 relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-full h-full overflow-hidden opacity-20 pointer-events-none">
                        <div className="absolute -top-[20%] -right-[10%] w-[70%] h-[140%] rounded-full bg-gradient-to-bl from-cyan-300 to-transparent blur-[120px]"></div>
                    </div>
                    
                    <div className="max-w-4xl mx-auto text-center space-y-8 relative z-10">
                        <h2 className="text-3xl md:text-5xl font-extrabold text-white tracking-tight">Taking Healthcare to the Last Mile</h2>
                        <div className="w-24 h-1.5 bg-cyan-400 mx-auto rounded-full opacity-80"></div>
                        <p className="text-lg md:text-2xl text-blue-100 font-light leading-relaxed max-w-3xl mx-auto">
                            Healthcare shouldn't depend on where a patient lives or where they were treated before.
                        </p>
                        <p className="text-lg md:text-2xl text-white font-medium leading-relaxed max-w-3xl mx-auto">
                            DoctorAI aims to connect patients with the right information, healthcare professional, diagnostic service and follow-up care — <span className="font-extrabold border-b-2 border-cyan-400 pb-1">when they need it.</span>
                        </p>
                        
                        <div className="pt-12">
                            <div className="inline-flex flex-col md:flex-row justify-center items-center gap-4 md:gap-8 bg-black/10 backdrop-blur-sm rounded-full py-4 px-8 md:px-12 border border-white/10 shadow-xl">
                                <span className="text-xl md:text-2xl font-bold">Better access.</span>
                                <span className="hidden md:inline text-cyan-400 text-lg">✦</span>
                                <span className="text-xl md:text-2xl font-bold">Earlier action.</span>
                                <span className="hidden md:inline text-cyan-400 text-lg">✦</span>
                                <span className="text-xl md:text-2xl font-bold">Better outcomes.</span>
                            </div>
                        </div>
                    </div>
                </section>

                {/* SECTION 7 — THE VISION */}
                <section className="py-24 px-4 md:px-8 max-w-6xl mx-auto text-center border-b border-gray-100">
                    <h2 className="text-3xl md:text-5xl font-extrabold text-gray-900 mb-6 tracking-tight">From Reactive Healthcare to <span className="text-blue-600">Proactive Health</span></h2>
                    <p className="text-lg md:text-2xl text-gray-500 mb-16 max-w-3xl mx-auto font-light leading-relaxed">
                        Today, healthcare often begins when something goes wrong. <br className="hidden md:block mt-2"/>
                        <span className="font-semibold text-gray-800">We believe the future should be different.</span>
                    </p>
                    
                    <div className="flex flex-wrap justify-center gap-4 md:gap-6">
                        {visionData.map((vision, i) => (
                            <div key={i} className="group flex items-center space-x-3 text-base md:text-lg font-bold text-gray-700 bg-white border border-gray-200 px-8 py-4 rounded-2xl shadow-[0_2px_15px_rgb(0,0,0,0.02)] hover:border-blue-500 hover:shadow-[0_10px_30px_rgba(59,130,246,0.15)] hover:-translate-y-1 transition-all duration-300 cursor-default">
                                <span className="text-blue-500 text-xl font-black group-hover:scale-125 transition-transform duration-300">✓</span>
                                <span>{vision}.</span>
                            </div>
                        ))}
                    </div>
                </section>

                <SolutionsSection />
                <BenefitsSection />
            </main>

            <Footer />
        </div>
    );
};

export default DoctorAI;